import {useEffect, useRef, useState} from 'preact/hooks';
import {groupMembers, switcherFromMetaobject} from '../../../../shared/product-switcher.js';
import {fetchMembershipGroups} from '../../../../shared/models/product-switcher';
import {createProductGroup, linkProductGroup, saveGroupMembers} from '../../../../shared/models/product-group';

export default function ProductGroupCard({session, adminUrl, disabled, onSaved, onBusyChange}) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selection, setSelection] = useState('new');
  const [members, setMembers] = useState([]);
  const [editing, setEditing] = useState(false);
  const connection = useRef(null);
  const creationId = useRef('');
  const linked = session?.published;
  const configuratorHref = (id) => {
    const path = `/products/${id.split('/').pop()}`;
    return __STANDALONE__ ? path : `${adminUrl.replace(/\/products\/[^/]+$/, '')}/apps/evp-configurator${path}`;
  };
  const busy = disabled || loading;
  useEffect(() => { onBusyChange(loading); return () => onBusyChange(false); }, [loading]);
  const openConnection = async () => {
    setError(''); setLoading(true); connection.current?.showOverlay();
    if (!creationId.current) creationId.current = crypto.randomUUID();
    try {
      const found = await fetchMembershipGroups(session.productId);
      setGroups(found); setSelection(found.some((group) => group.id === linked?.id) ? linked.id : found[0]?.id || 'new');
    } catch (e) { setError(e instanceof Error ? e.message : 'Unable to find groups.'); }
    finally { setLoading(false); }
  };
  const openEditor = () => { setError(''); setMembers(groupMembers(linked)); setEditing(true); };
  const save = async (editMembers) => {
    setLoading(true); setError('');
    try {
      const next = editMembers ? await saveGroupMembers(session, members) : selection === 'new'
        ? await createProductGroup(session, creationId.current) : await linkProductGroup(session, selection);
      onSaved(next);
      if (editMembers) setEditing(false);
      else connection.current?.hideOverlay();
      creationId.current = ''; shopify.toast.show(editMembers ? 'Shared group members saved' : 'Group selected. Save draft, then publish to apply.');
    } catch (e) { setError(e instanceof Error ? e.message : 'Unable to save group.'); }
    finally { setLoading(false); }
  };
  const finishEditing = () => {
    const currentIds = groupMembers(linked).map(({productId}) => productId);
    const nextIds = members.map(({productId}) => productId);
    if (JSON.stringify(currentIds) === JSON.stringify(nextIds)) setEditing(false);
    else save(true);
  };
  const add = async () => {
    setLoading(true); setError('');
    try {
      const result = await shopify.resourcePicker({type: 'product', multiple: false, action: 'select', filter: {variants: false, draft: false, archived: false}});
      const product = result?.[0];
      if (product) {
        if (members.some((member) => member.productId === product.id)) throw new Error('This product is already a member.');
        setMembers([...members, {productId: product.id, title: product.title, handle: product.handle || ''}]);
      }
    } catch (e) { setError(e instanceof Error ? e.message : 'Unable to choose product.'); }
    finally { setLoading(false); }
  };
  return <s-section>
    <s-stack gap="base">
      <s-grid gridTemplateColumns="1fr auto" gap="small" alignItems="center">
        <s-heading>Group</s-heading>
        <s-stack direction="inline" gap="small-100">
          {linked && (editing
            ? <s-button variant="tertiary" icon="check" accessibilityLabel="Done editing group products" disabled={busy || !members.length} onClick={finishEditing} />
            : <s-button variant="tertiary" icon="edit" accessibilityLabel="Edit group products" disabled={busy} onClick={openEditor} />)}
          {!editing && <s-button variant="tertiary" icon="database-connect" accessibilityLabel="Connect product group" disabled={busy || !session} onClick={openConnection} />}
        </s-stack>
      </s-grid>
      {linked ? <s-stack direction="block" gap="small">
        {editing && <s-text color="subdued">Changes made to the group will apply to other products.</s-text>}
        {(editing ? members : groupMembers(linked)).map((member, index) => <s-stack key={member.productId} direction="block" gap="small">
        {index > 0 && <s-divider />}
        {editing ? <s-grid gridTemplateColumns="minmax(0, 1fr) auto" gap="small" alignItems="start">
          <s-link tone="neutral" href={configuratorHref(member.productId)} target="_blank"><span style={{fontSize:'12px', lineHeight:'16px', color:'var(--p-color-text, #303030)'}}>{member.title || 'Missing or inaccessible product'}</span></s-link>
          <s-button variant="tertiary" tone="critical" icon="delete" accessibilityLabel={`Remove ${member.title || 'product'} from group`} disabled={busy} onClick={() => setMembers(members.filter((item) => item.productId !== member.productId))} />
        </s-grid> : <s-link tone="neutral" href={configuratorHref(member.productId)} target="_blank"><span style={{fontSize:'12px', lineHeight:'16px', color:'var(--p-color-text, #303030)'}}>{member.title || 'Missing or inaccessible product'}</span></s-link>}
      </s-stack>)}
        {editing && members.length < 5 && <s-link onClick={add}>Add product</s-link>}
        {editing && error && <s-banner tone="critical">{error}</s-banner>}
      </s-stack> : <s-paragraph color="subdued">Grouping lets you interlink products that are different versions of the same thing.</s-paragraph>}
    </s-stack>
    <s-modal ref={connection} id="product-group-connection" heading="Set up group">
      <s-stack gap="base">
        <s-paragraph>Select an existing group or create a new one. This product's selection takes effect after Save draft and Publish. Creating or editing the shared group is immediate.</s-paragraph>
        {loading ? <s-spinner accessibilityLabel="Loading groups" /> : <s-select label="Group" value={selection} disabled={busy} onChange={(event) => setSelection(event.currentTarget.value)}>
          {[...groups.map((group) => ({id: group.id, name: switcherFromMetaobject(group).internalName || 'Unnamed group'})), {id: 'new', name: 'New group'}].map((option) => <s-option key={option.id} value={option.id}>{option.name}</s-option>)}
        </s-select>}
        {error && <s-banner tone="critical">{error}</s-banner>}
      </s-stack>
      <s-button slot="secondary-actions" commandFor="product-group-connection" command="--hide" disabled={busy}>Cancel</s-button>
      <s-button slot="primary-action" variant="primary" disabled={busy} onClick={() => save(false)}>Select group</s-button>
    </s-modal>
  </s-section>;
}
